import type { GameCommand, InputSource } from '../core/types';
import { classifyGesture, commandForKey, isSlideKey, type PointerSample } from './swipe';

export class PointerKeyboardInput implements InputSource {
  private readonly queue: GameCommand[] = [];
  private readonly heldSlideKeys = new Set<string>();
  private activePointerId: number | undefined;
  private startSample: PointerSample | undefined;

  public constructor(private readonly target: HTMLElement) {
    target.addEventListener('pointerdown', this.onPointerDown);
    target.addEventListener('pointerup', this.onPointerUp);
    target.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onWindowBlur);
  }

  public drain(): readonly GameCommand[] {
    if (this.queue.length === 0) return [];
    return this.queue.splice(0, this.queue.length);
  }

  public dispose(): void {
    this.target.removeEventListener('pointerdown', this.onPointerDown);
    this.target.removeEventListener('pointerup', this.onPointerUp);
    this.target.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onWindowBlur);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.activePointerId !== undefined || !event.isPrimary) return;
    this.activePointerId = event.pointerId;
    this.startSample = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    this.target.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId || this.startSample === undefined) return;
    const command = classifyGesture(this.startSample, {
      x: event.clientX,
      y: event.clientY,
      time: event.timeStamp,
    });
    if (command !== undefined) this.queue.push(command);
    this.clearPointer(event.pointerId);
    event.preventDefault();
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId) this.clearPointer(event.pointerId);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isSlideKey(event.key)) {
      const key = event.key.toLowerCase();
      if (!this.heldSlideKeys.has(key)) {
        const wasReleased = this.heldSlideKeys.size === 0;
        this.heldSlideKeys.add(key);
        if (wasReleased) this.queue.push({ type: 'setSlideHeld', held: true });
      }
      event.preventDefault();
      return;
    }

    const command = commandForKey(event.key);
    if (command === undefined) return;
    this.queue.push(command);
    event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (!isSlideKey(event.key)) return;
    const wasHeld = this.heldSlideKeys.delete(event.key.toLowerCase());
    if (wasHeld && this.heldSlideKeys.size === 0) {
      this.queue.push({ type: 'setSlideHeld', held: false });
    }
    event.preventDefault();
  };

  private readonly onWindowBlur = (): void => {
    if (this.heldSlideKeys.size === 0) return;
    this.heldSlideKeys.clear();
    this.queue.push({ type: 'setSlideHeld', held: false });
  };

  private clearPointer(pointerId: number): void {
    if (this.target.hasPointerCapture(pointerId)) this.target.releasePointerCapture(pointerId);
    this.activePointerId = undefined;
    this.startSample = undefined;
  }
}
